import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Loader2, Plug, CheckCircle2, XCircle, ArrowUpRight, ShieldCheck, Gauge, Inbox, Clock, MessageSquare, Copy, HelpCircle, ChevronDown, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const QUALITY = {
  GREEN: { label: 'High quality', cls: 'border-emerald-500/40 text-emerald-500' },
  YELLOW: { label: 'Medium quality', cls: 'border-amber-500/40 text-amber-500' },
  RED: { label: 'Low quality', cls: 'border-red-500/40 text-red-500' },
};

const timeAgo = (ts) => {
  if (!ts) return null;
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return null;
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export const WhatsAppConnectionPanel = () => {
  const [conn, setConn] = useState(undefined);
  const [numInfo, setNumInfo] = useState(null);
  const [activating, setActivating] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testChecking, setTestChecking] = useState(false);
  const [testPassed, setTestPassed] = useState(false);
  const [testListening, setTestListening] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const baselineInbound = useRef(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const fetchStatus = () => api.get('/api/social-connectors/status')
    .then((r) => {
      const data = r.data?.connections;
      const wa = Array.isArray(data)
        ? data.find((c) => c.platform === 'whatsapp')
        : (data?.whatsapp || null);
      setConn(wa || null);
      return wa || null;
    })
    .catch(() => { setConn(null); return null; });

  useEffect(() => {
    fetchStatus();
    api.get('/api/social-connectors/whatsapp/number-info').then((r) => setNumInfo(r.data)).catch(() => {});
  }, []);

  const activateInbound = async () => {
    setActivating(true);
    try {
      const r = await api.post('/api/social-connectors/whatsapp/activate-inbound');
      if (r.data?.subscribed) {
        toast.success(r.data.message || 'Inbound activated. Send a test message to confirm.');
      } else {
        toast.error(r.data?.detail || 'Could not activate inbound. Check credentials & webhook config.');
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to activate inbound.');
    } finally {
      setActivating(false);
    }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setTestListening(false);
  };

  const pollOnce = async () => {
    const wa = await fetchStatus();
    const latest = wa?.last_message_at || null;
    if (latest && latest !== baselineInbound.current) {
      setTestPassed(true);
      stopPolling();
      toast.success('Inbound received! Your webhook is working end-to-end.');
      return true;
    }
    return false;
  };

  const openTest = () => {
    stopPolling();
    baselineInbound.current = conn?.last_message_at || null;
    setTestPassed(false);
    setTestOpen(true);
    // Auto-listen for ~30s so it lights up green on its own.
    setTestListening(true);
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 3;
      const hit = await pollOnce();
      if (hit || elapsed >= 30) stopPolling();
    }, 3000);
  };

  const closeTest = () => { stopPolling(); setTestOpen(false); };

  useEffect(() => () => stopPolling(), []);

  const checkInbound = async () => {
    setTestChecking(true);
    try {
      const hit = await pollOnce();
      if (!hit) toast.info('No new inbound yet. Send "Hi" from a customer phone, then check again.');
    } finally {
      setTestChecking(false);
    }
  };

  const copyNumber = () => {
    const phone = conn?.credentials?.display_phone_number || conn?.credentials?.phone_number || conn?.phone_number || '';
    if (phone) { navigator.clipboard?.writeText(phone); toast.success('Number copied'); }
  };



  if (conn === undefined) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const connected = !!conn?.is_connected;
  const cred = conn?.credentials || {};
  const phone = numInfo?.display_phone_number || cred.display_phone_number || cred.phone_number || conn?.phone_number;
  const quality = QUALITY[((numInfo?.quality_rating || conn?.quality_rating) || '').toUpperCase()];
  const tier = numInfo?.messaging_tier || conn?.messaging_tier || conn?.tier;
  const numSyncedAt = numInfo?.synced_at || conn?.number_synced_at;

  return (
    <div className="space-y-4" data-testid="whatsapp-connection-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${connected ? 'bg-emerald-500/10' : 'bg-muted'}`}>
                <Plug className={`h-5 w-5 ${connected ? 'text-emerald-500' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  WhatsApp Business
                  {connected
                    ? <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[11px]" data-testid="wa-conn-status"><CheckCircle2 className="h-3 w-3 mr-1" />Connected</Badge>
                    : <Badge variant="outline" className="text-muted-foreground text-[11px]" data-testid="wa-conn-status"><XCircle className="h-3 w-3 mr-1" />Not connected</Badge>}
                </CardTitle>
                <CardDescription>{phone ? `Number: ${phone}` : 'Connect your WhatsApp Business number via Meta Embedded Signup'}</CardDescription>
              </div>
            </div>
            <Button size="sm" variant={connected ? 'outline' : 'default'} onClick={() => navigate('/social-connectors')} data-testid="wa-conn-manage-btn">
              {connected ? 'Manage' : 'Connect WhatsApp'}<ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </CardHeader>
        {connected && (
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><ShieldCheck className="h-3.5 w-3.5" />Number quality</div>
                {quality
                  ? <Badge variant="outline" className={`${quality.cls} text-xs`}>{quality.label}</Badge>
                  : <span className="text-sm text-muted-foreground">Not rated yet <span className="text-[11px]">(normal for new numbers)</span></span>}
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Gauge className="h-3.5 w-3.5" />Messaging tier</div>
                <span className="text-sm font-medium">{tier ? `${tier} / day` : 'Standard'}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              {phone && (
                <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="wa-conn-number">
                  <MessageSquare className="h-3.5 w-3.5" />Number <span className="text-foreground font-medium">{phone}</span>
                </span>
              )}
              {numSyncedAt && (
                <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="wa-number-synced">
                  <Clock className="h-3 w-3" />Synced {timeAgo(numSyncedAt)}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Delivery & read receipts are reconciled automatically from Meta webhook status events — zeros are normal until your number starts receiving live traffic. Manage webhook + number settings in Social Connectors.
            </p>

            {/* Inbound activation — subscribes this WABA to Meta webhooks so
                incoming customer replies actually reach Lockene. */}
            <div className="mt-3 rounded-lg border border-border p-3 flex items-center justify-between gap-3" data-testid="wa-inbound-status">
              <div className="flex items-center gap-2 text-sm">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Inbound messages</span>
                {conn?.webhook_subscribed
                  ? <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[11px]" data-testid="wa-inbound-badge"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>
                  : <Badge variant="outline" className="border-amber-500/40 text-amber-500 text-[11px]" data-testid="wa-inbound-badge"><XCircle className="h-3 w-3 mr-1" />Not subscribed</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={openTest} data-testid="wa-test-inbound-btn">
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />Test inbound
                </Button>
                <Button size="sm" variant="outline" onClick={activateInbound} disabled={activating} data-testid="wa-activate-inbound-btn">
                  {activating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Activating…</> : (conn?.webhook_subscribed ? 'Re-check' : 'Activate inbound')}
                </Button>
              </div>
            </div>

            {/* Webhook health — last time Meta actually delivered an inbound event */}
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground pl-1" data-testid="wa-webhook-health">
              <Clock className="h-3.5 w-3.5" />
              {conn?.last_message_at
                ? <>Last inbound received <span className="text-foreground font-medium">{timeAgo(conn.last_message_at)}</span></>
                : <span className="text-amber-500/90">No inbound received yet — run "Test inbound" to confirm the webhook.</span>}
            </div>

            {!conn?.webhook_subscribed && (
              <p className="text-xs text-amber-500/90 mt-2" data-testid="wa-inbound-hint">
                If customer replies aren't showing, click "Activate inbound" — this subscribes your WhatsApp number to Meta's message webhooks.
              </p>
            )}

            {/* Collapsible "How WhatsApp works" help panel */}
            <div className="mt-3 rounded-lg border border-border" data-testid="wa-help-panel">
              <button
                onClick={() => setShowHelp((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="wa-help-toggle"
              >
                <span className="flex items-center gap-2"><HelpCircle className="h-4 w-4" />How WhatsApp inbound & outbound work</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showHelp ? 'rotate-180' : ''}`} />
              </button>
              {showHelp && (
                <div className="px-3 pb-3 space-y-3 text-xs" data-testid="wa-help-content">
                  <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-2.5">
                    <div className="flex items-center gap-1.5 font-semibold text-emerald-500 mb-1"><ArrowUpRight className="h-3.5 w-3.5" />Outbound (you → customer)</div>
                    <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
                      <span className="px-1.5 py-0.5 rounded bg-muted">Lockene</span><ArrowRight className="h-3 w-3" />
                      <span className="px-1.5 py-0.5 rounded bg-muted">Meta Cloud API</span><ArrowRight className="h-3 w-3" />
                      <span className="px-1.5 py-0.5 rounded bg-muted">Customer's WhatsApp</span>
                    </div>
                    <p className="text-muted-foreground mt-1.5">Window <b>closed</b> → must send an approved <b>template</b> (billable, needs card). Window <b>open</b> → free session text. Meta returns ticks: sent ✓ → delivered ✓✓ → read.</p>
                  </div>
                  <div className="rounded-md bg-blue-500/5 border border-blue-500/20 p-2.5">
                    <div className="flex items-center gap-1.5 font-semibold text-blue-400 mb-1"><Inbox className="h-3.5 w-3.5" />Inbound (customer → you)</div>
                    <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
                      <span className="px-1.5 py-0.5 rounded bg-muted">Customer sends</span><ArrowRight className="h-3 w-3" />
                      <span className="px-1.5 py-0.5 rounded bg-muted">Meta webhook</span><ArrowRight className="h-3 w-3" />
                      <span className="px-1.5 py-0.5 rounded bg-muted">Lockene thread</span><ArrowRight className="h-3 w-3" />
                      <span className="px-1.5 py-0.5 rounded bg-muted">AI auto-reply</span>
                    </div>
                    <p className="text-muted-foreground mt-1.5">Needs two things: the WABA <b>subscribed</b> to the app (the "Activate inbound" button) <b>and</b> the Meta App's webhook Callback URL verified with the <b>messages</b> field on.</p>
                  </div>
                  <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-2.5 text-muted-foreground">
                    <b className="text-amber-500">24-hour window:</b> when a customer messages you, a free 24h window opens — free replies + free utility templates. After it closes, only paid templates can start a new conversation.
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Inbound test checklist */}
      <Dialog open={testOpen} onOpenChange={(o) => { if (!o) closeTest(); else setTestOpen(true); }}>
        <DialogContent data-testid="wa-inbound-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-emerald-500" />Test inbound message</DialogTitle>
            <DialogDescription>Confirm a customer reply actually reaches Lockene, in 3 steps.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold shrink-0">1</span>
              <div className="text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  From any phone, send <span className="font-semibold">"Hi"</span> to your business number
                  {phone && <button onClick={copyNumber} className="inline-flex items-center gap-1 text-emerald-500 hover:underline" data-testid="wa-test-copy-number"><span className="font-medium">{phone}</span><Copy className="h-3 w-3" /></button>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">This also opens the free 24h window, so replies deliver even with ₹0 balance.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold shrink-0">2</span>
              <div className="text-sm">Lockene is listening — it detects your message automatically.</div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-semibold shrink-0">3</span>
              <div className="text-sm">Done! Or click <span className="font-semibold">Check now</span> to poll immediately.</div>
            </div>
            {testListening && !testPassed && (
              <div className="rounded-lg border border-border bg-muted/20 p-3 flex items-center gap-2 text-sm text-muted-foreground" data-testid="wa-test-listening">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />Listening for your message… (auto-checks for 30s)
              </div>
            )}
            {testPassed && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2 text-sm text-emerald-500" data-testid="wa-test-result">
                <CheckCircle2 className="h-4 w-4" />Inbound received — your webhook is working end-to-end! 🎉
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeTest} data-testid="wa-test-close-btn">Close</Button>
            <Button onClick={checkInbound} disabled={testChecking} data-testid="wa-test-check-btn">
              {testChecking ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Checking…</> : 'Check now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppConnectionPanel;
